import type { Firewall } from "@cloudserver/contracts";
import { Link, useNavigate } from "@tanstack/react-router";
import { ExternalLink, Plus, ShieldCheck, Trash2 } from "lucide-react";
import { useState } from "react";
import { useTranslation } from "react-i18next";
import { useCanWrite, useReadOnly } from "../api/users.js";
import { useDeleteFirewall, useFirewalls } from "../api/firewalls.js";
import { progressOf, useRunningOperations } from "../api/networks.js";
import { DangerDialog } from "../ui/confirm.js";
import { ErrorMessage } from "../ui/feedback.js";
import { relativeTime } from "../ui/format.js";
import { EmptyState, PageHeader } from "../ui/layout.js";
import { Alert, Button } from "../ui/primitives.js";
import { DataTable } from "../ui/table.js";
import { useToast } from "../ui/toast.js";
import { failureToast } from "./networks.js";

/** True while the firewall is applied to a server or a label selector. */
export const isApplied = (f: Firewall): boolean =>
  f.appliedTo.servers.length > 0 || f.appliedTo.labelSelectors.length > 0;

export function FirewallsPage({ projectId }: { projectId: string }) {
  const { t, i18n } = useTranslation();
  // Guests may only look (docs/users.md): no create action; the API refuses them anyway.
  const canWrite = useCanWrite(projectId);
  const firewalls = useFirewalls(projectId);
  const running = useRunningOperations(projectId);
  const remove = useDeleteFirewall(projectId);
  const toast = useToast();
  const navigate = useNavigate();
  const [deleting, setDeleting] = useState<Firewall | null>(null);
  const { readOnly, reason: readOnlyReason, token: tokenReadOnly } = useReadOnly(projectId);

  const createButton = readOnly ? (
    <Button disabled icon={<Plus className="size-4" aria-hidden />}>
      {t("firewalls.create")}
    </Button>
  ) : (
    <Link
      to="/projects/$projectId/firewalls/new"
      params={{ projectId }}
      className="inline-flex min-h-10 items-center justify-center gap-2 rounded-lg bg-accent px-4 text-sm font-medium text-accent-fg hover:bg-accent-strong"
    >
      <Plus className="size-4" aria-hidden />
      {t("firewalls.create")}
    </Link>
  );
  const open = (f: Firewall) =>
    void navigate({
      to: "/projects/$projectId/firewalls/$firewallId",
      params: { projectId, firewallId: f.id },
    });

  return (
    <>
      <PageHeader
        title={t("nav.firewalls")}
        intro={t("firewalls.intro")}
        help="firewalls"
        actions={canWrite && firewalls.data?.length ? createButton : undefined}
      />
      {tokenReadOnly && <Alert tone="warn">{readOnlyReason}</Alert>}
      {firewalls.error ? (
        <ErrorMessage error={firewalls.error} />
      ) : (
        <DataTable<Firewall>
          label={t("nav.firewalls")}
          rows={firewalls.data}
          rowKey={(f) => f.id}
          initialSort={{ key: "name", dir: "asc" }}
          progress={(f) => progressOf(running.data, f.id)}
          empty={
            <EmptyState
              icon={<ShieldCheck className="size-6" aria-hidden />}
              title={t("firewalls.empty")}
              action={canWrite ? createButton : undefined}
              help="firewalls"
            >
              {t("firewalls.emptyLead")}
            </EmptyState>
          }
          columns={[
            {
              key: "name",
              header: t("firewalls.name"),
              primary: true,
              sort: (f) => f.name.toLowerCase(),
              cell: (f) => (
                <Link
                  to="/projects/$projectId/firewalls/$firewallId"
                  params={{ projectId, firewallId: f.id }}
                  className="font-medium text-accent-strong hover:underline"
                >
                  {f.name}
                </Link>
              ),
            },
            {
              key: "rules",
              header: t("firewalls.rules"),
              sort: (f) => f.rules.length,
              cell: (f) =>
                t("firewalls.rulesCount", {
                  inbound: f.rules.filter((r) => r.direction === "in").length,
                  outbound: f.rules.filter((r) => r.direction === "out").length,
                }),
            },
            {
              key: "applied",
              header: t("firewalls.appliedTo"),
              sort: (f) => f.appliedTo.servers.length + f.appliedTo.labelSelectors.length,
              cell: (f) =>
                isApplied(f) ? (
                  t("firewalls.appliedCount", {
                    servers: f.appliedTo.servers.length,
                    selectors: f.appliedTo.labelSelectors.length,
                  })
                ) : (
                  <span className="text-muted">{t("firewalls.appliedNothing")}</span>
                ),
            },
            {
              key: "created",
              header: t("firewalls.createdOn"),
              hideOnPhone: true,
              sort: (f) => f.createdAt,
              cell: (f) => (
                <span className="text-muted">{relativeTime(f.createdAt, i18n.language)}</span>
              ),
            },
          ]}
          rowMenu={(f) => [
            [
              {
                label: t("firewalls.open"),
                icon: <ExternalLink className="size-4" aria-hidden />,
                onSelect: () => open(f),
              },
            ],
            [
              {
                label: t("firewalls.delete"),
                icon: <Trash2 className="size-4" aria-hidden />,
                danger: true,
                disabled: readOnly || isApplied(f),
                reason: readOnly ? readOnlyReason : t("firewalls.inUseReason"),
                onSelect: () => setDeleting(f),
              },
            ],
          ]}
        />
      )}
      <DangerDialog
        open={deleting !== null}
        onOpenChange={(next) => !next && setDeleting(null)}
        title={t("firewalls.deleteTitle", { name: deleting?.name ?? "" })}
        consequences={[t("firewalls.deleteRules"), t("firewalls.deleteNoEffect")]}
        actionLabel={t("firewalls.delete")}
        busy={remove.isPending}
        onConfirm={() =>
          deleting &&
          remove.mutate(deleting.id, {
            onSuccess: () => {
              toast({ tone: "ok", title: t("firewalls.deleted", { name: deleting.name }) });
              setDeleting(null);
            },
            onError: (error) => toast(failureToast(t, error)),
          })
        }
      />
    </>
  );
}
